<template>
  <div v-if="$store.state.isUserLoggedIn">

    <!-- ── New notifications ────────────────────────────────── -->
    <v-sheet :rounded="isSmallerDisplay() ? '' : 'lg'" elevation="1" class="noti-card">
      <!-- Header -->
      <div class="d-flex align-center px-4 pt-3 pb-2">
        <v-icon size="18" class="mr-2" color="primary">mdi-bell-ring-outline</v-icon>
        <span class="text-subtitle-2 font-weight-bold">{{ $t("message.new") }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="reload()" class="mr-1">
          <v-icon size="18">mdi-sync</v-icon>
        </v-btn>
        <v-btn icon small @click="clearAll()">
          <v-icon size="18">mdi-delete-sweep-outline</v-icon>
        </v-btn>
      </div>

      <!-- Empty state -->
      <div v-if="!notifications || notifications.length == 0" class="text-center py-6 px-4">
        <v-icon size="40" class="grey--text mb-2">mdi-bell-off-outline</v-icon>
        <div class="text-subtitle-2 grey--text">{{ $t("message.no_notifications") }}</div>
        <div class="text-caption grey--text">{{ $t("message.go_train") }}</div>
      </div>

      <!-- Notification rows -->
      <div v-for="(n, index) in notifications" :key="n._id + index">
        <div class="noti-row d-flex align-start px-3 py-3 clickable" @click="viewDetails(n)">
          <!-- Unread dot -->
          <div class="noti-dot-col d-flex align-start justify-center pt-1 mr-1">
            <div class="noti-unread-dot"></div>
          </div>
          <!-- Avatar -->
          <v-avatar size="42" class="mr-3 flex-shrink-0">
            <v-img v-if="isSystemMessage(n.type)" :src="assets('system.png')" />
            <v-img v-else-if="n.academy && n.academy.image" :src="userImg(n.academy.image)"
              @click.stop="navigateTo({ name: 'viewacademy', params: { academyId: n.academy._id } })" />
            <v-img v-else-if="n.sender.userImage" :src="userImg(n.sender.userImage)"
              @click.stop="navigateTo({ name: 'userprofile', params: { userId: n.sender._id } })" />
            <v-img v-else :src="assets('blank-profile-picture.png')"
              @click.stop="navigateTo({ name: 'userprofile', params: { userId: n.sender._id } })" />
          </v-avatar>
          <!-- Content -->
          <div class="flex-grow-1" style="min-width:0">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center" style="min-width:0;flex:1">
                <NotificationIcon class="mr-1 flex-shrink-0" :type="n.type"></NotificationIcon>
                <span class="text-body-2 font-weight-bold text-truncate"
                  v-html="n.titleTranslated ? n.titleTranslated : n.title"></span>
              </div>
              <span class="text-caption grey--text flex-shrink-0 ml-2">
                {{ $d(new Date(n.time), "short", $i18n.locale) }}
              </span>
            </div>
            <div class="text-body-2 grey--text mt-1 noti-body-clamp">
              {{ n.contentsTranslated ? n.contentsTranslated : n.contents }}
            </div>
            <!-- Recipient badge -->
            <div v-if="n.originalRecipient" class="mt-1">
              <UserAvatar :user="n.originalRecipient" size="20"></UserAvatar>
            </div>
            <!-- Accept / reject -->
            <div v-if="requiresResponse(n)" class="d-flex mt-2" style="gap:8px" @click.stop>
              <v-btn x-small rounded elevation="0" color="success" dark @click.stop="processResponse(n, 'accept')">
                <v-icon x-small class="mr-1">mdi-check</v-icon>
                {{ $t('message.accept') }}
              </v-btn>
              <v-btn x-small rounded elevation="0" color="error" dark @click.stop="processResponse(n, 'reject')">
                <v-icon x-small class="mr-1">mdi-close</v-icon>
                {{ $t('message.reject') }}
              </v-btn>
            </div>
          </div>
          <!-- Dismiss -->
          <v-btn icon x-small class="ml-2 mt-1 flex-shrink-0" v-if="!requiresResponse(n)"
            @click.stop="readNotification(n)">
            <v-icon size="16" color="grey lighten-1">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider v-if="index + 1 < notifications.length" :key="'d' + index" class="noti-divider"></v-divider>
      </div>

      <v-skeleton-loader v-if="loadingNew"
        type="list-item-avatar-three-line, list-item-avatar-three-line" class="px-2 pb-2">
      </v-skeleton-loader>
      <div v-if="viewMoreNew" class="text-center pb-3 pt-1">
        <v-btn small elevation="0" color="primary" outlined rounded @click="getNewNotifications(true)">
          {{ $t('message.view_more') }}
        </v-btn>
      </div>
    </v-sheet>

    <!-- ── Read / opened notifications ─────────────────────── -->
    <v-sheet class="mt-3 noti-card" :rounded="isSmallerDisplay() ? '' : 'lg'" elevation="1">
      <!-- Header -->
      <div class="d-flex align-center px-4 pt-3 pb-2">
        <v-icon size="18" class="mr-2 grey--text">mdi-bell-check-outline</v-icon>
        <span class="text-subtitle-2 font-weight-medium grey--text">{{ $t("message.opened") }}</span>
      </div>

      <!-- Read rows -->
      <div v-for="(n, index) in notificationsRead" :key="n._id + index">
        <div class="noti-row noti-row--read d-flex align-start px-3 py-3 clickable" @click="viewDetails(n)">
          <!-- Spacer for dot column -->
          <div class="noti-dot-col mr-1"></div>
          <!-- Avatar -->
          <v-avatar size="42" class="mr-3 flex-shrink-0">
            <v-img v-if="isSystemMessage(n.type)" :src="assets('system.png')" />
            <v-img v-else-if="n.academy && n.academy.image" :src="userImg(n.academy.image)"
              @click.stop="navigateTo({ name: 'viewacademy', params: { academyId: n.academy._id } })" />
            <v-img v-else-if="n.sender.userImage" :src="userImg(n.sender.userImage)"
              @click.stop="navigateTo({ name: 'userprofile', params: { userId: n.sender._id } })" />
            <v-img v-else :src="assets('blank-profile-picture.png')"
              @click.stop="navigateTo({ name: 'userprofile', params: { userId: n.sender._id } })" />
          </v-avatar>
          <!-- Content -->
          <div class="flex-grow-1" style="min-width:0">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center" style="min-width:0;flex:1">
                <NotificationIcon class="mr-1 flex-shrink-0" :type="n.type"></NotificationIcon>
                <span class="text-body-2 text-truncate grey--text"
                  v-html="n.titleTranslated ? n.titleTranslated : n.title"></span>
              </div>
              <span class="text-caption grey--text flex-shrink-0 ml-2">
                {{ $d(new Date(n.time), "short", $i18n.locale) }}
              </span>
            </div>
            <div class="text-body-2 grey--text mt-1 noti-body-clamp">
              {{ n.contentsTranslated ? n.contentsTranslated : n.contents }}
            </div>
            <div v-if="n.originalRecipient" class="mt-1">
              <UserAvatar :user="n.originalRecipient" size="20"></UserAvatar>
            </div>
            <div v-if="requiresResponse(n)" class="d-flex mt-2" style="gap:8px" @click.stop>
              <v-btn x-small rounded elevation="0" color="success" dark @click.stop="processResponse(n, 'accept')">
                <v-icon x-small class="mr-1">mdi-check</v-icon>
                {{ $t('message.accept') }}
              </v-btn>
              <v-btn x-small rounded elevation="0" color="error" dark @click.stop="processResponse(n, 'reject')">
                <v-icon x-small class="mr-1">mdi-close</v-icon>
                {{ $t('message.reject') }}
              </v-btn>
            </div>
          </div>
        </div>
        <v-divider v-if="index + 1 < notificationsRead.length" :key="'d' + index" class="noti-divider"></v-divider>
      </div>

      <v-skeleton-loader v-if="loadingRead"
        type="list-item-avatar-three-line, list-item-avatar-three-line" class="px-2 pb-2">
      </v-skeleton-loader>
      <div v-if="viewMoreRead" class="text-center pb-3 pt-1">
        <v-btn small outlined color="primary" rounded @click="getReadNotifications(true)">
          {{ $t('message.view_more') }}
        </v-btn>
      </div>
    </v-sheet>

  </div>
</template>

<script>
import NotificationIcon from "@/components/displays/NotificationIconDisplay.vue";
import UserAvatar from "@/components/displays/UserAvatarDisplay.vue";
import EventService from "@/services/EventService";
import NotificationService from "@/services/NotificationService";
import UserService from "@/services/UserService";

export default {
  components: {
    NotificationIcon,
    UserAvatar,
  },
  data() {
    return {
      notifications: [],
      notificationsRead: [],
      pointerRead: 0,
      pointerNew: 0,
      loadingNew: true,
      loadingRead: true,
      viewMoreNew: false,
      viewMoreRead: false,
      types: {
        NOTI_NEWEVENTPARTICIPANT: "new_event_participant", //EVENT
        NOTI_NEWEVENTMANAGER: "new_event_managers", //EVENT
        NOTI_EVENTINVITE: "event_invite", //EVENT
        NOTI_EVENTINVITEACCEPTED: "event_invite_accepted", //EVENT
        NOTI_NEWLINK: "new_link", //LINK
        NOTI_NEWACADEMYMEMBER: "new_academy_member", //ACADEMY
        NOTI_NEWACADEMYMANAGER: "new_academy_manager", //ACADEMY
        NOTI_ACADEMYINVITE: "academy_invite", //ACADEMY
        NOTI_POSTLIKED: "post_liked", //LIKE
        NOTI_COMMENTLIKED: "comment_liked", //LIKE
        NOTI_VIDEOLIKED: "video_liked", //LIKE
        NOTI_ACADEMYLIKED: "academy_liked", //LIKE
        NOTI_ACADEMYREVIEWED: "academy_reviewed", //LIKE
        NOTI_POSTTAG: "post_tag", //TAG
        NOTI_COMMENTTAG: "comment_tag", //TAG
        NOTI_POSTCOMMENTED: "post_commented", //COMMENT
        NOTI_VIDEOCOMMENTED: "video_commented", //COMMENT
        NOTI_VIDEOREADY: "video_ready",
        NOTI_VIDEODELETED: "video_deleted",
        NOTI_REPOST: "post_reposted", //REPOST
        NOTI_ACADEMYMESSAGE: "academy_message", //REPOST
        NOTI_PAYMENTREQUEST: "payment_request", //Payment request
        NOTI_CC_UPDATE_REQUEST: "cc_update_request", //Credit card update
        NOTI_PAYMENTCONFIRMATION: "payment_confirmation", //Payment request
        NOTI_NEWACADEMYVIDEO: "new_academy_video", //New academy video
        NOTI_CLASSREQUESTACCEPTED: "class_request_accepted", //CLASS ACCEPTED
        NOTI_CLASSREQUESTREJECTED: "class_request_rejected", //REQUEST REJECTED
        NOTI_CLASSATENDEELEFT: "class_atendee_left", //ATENDEE LEFT
        NOTI_CLASSREQUEST: "class_request", //CLASS REQUEST
        NOTI_DOCUMENTREQUEST: "document_signature_request", //Signature request
        NOTI_DOCUMENTREQUESTSURVEY: "document_survey_request",
        NOTI_BELTVERIFIED: "belt_verified", //Signature request,
        NOTI_BELTREJECTED: "belt_rejected",
        NOTI_POSTOSS: "post_ossed",
        NOTI_POSTFIRE: "post_fire",
        NOTI_POSTRNC: "post_rnc",
        NOTI_ACADEMYPOST: "academy_post",
        NOTI_COMMENTREPLIED: "comment_replied", //COMMENT
        NOTI_PROGRAMASSIGNED: "program_assigned", //COMMENT
        NOTI_SAVECOMPETITORALERT: "saved_competitor_alert",//Tourney 
        NOTI_JOINEDCLASS: "joined_class",//Joined Class 
        NOTI_NEWACADEMYVISITOR: "new_academy_visitor", //NEW VISITOR
        NOTI_NEWACADEMYINQUIRY: "new_academy_inquiry" //NEW INQUIRY
      },
    };
  },
  created() {
    NotificationService.updateCount();
    this.getNewNotifications();
    this.getReadNotifications();
    var vm = this;
    vm.getMessagesCount(function (countM) {
      vm.getNotificationsCount(function (countS) {
        vm.$store.dispatch("setMessagesCount", countM);
        vm.$store.dispatch("setNotifications", countS);
        if (countM + countS == 0) {
          NotificationService.clearMobile();
        }
      });
    });
  },
  methods: {
    doNothing() { },
    reload() {
      this.pointerNew = 0;
      NotificationService.updateCount();
      this.getNewNotifications();
      this.getReadNotifications();
    },
    clearAll() {
      NotificationService.clearAllNotifications()
        .then((data) => {
          if (data) {
            let tmp = this.notifications;
            tmp = tmp.reverse();
            this.notifications = [];
            for (const n of tmp)
              this.notificationsRead.unshift(n);
            this.$store.dispatch("setNotifications", 0);
          }
        });
    },
    requiresResponse(n) {
      if (n.type == this.types.NOTI_EVENTINVITE) {
        return true;
      } else {
        return false;
      }
    },
    processResponse(n, action) {
      if (n.type == this.types.NOTI_EVENTINVITE) {
        if (action == "accept") {
          EventService.acceptInvite(n.objectId)
            .then((data) => {
              if (data) {
                this.readNotification(n);
              }
            });
        } else {
          EventService.rejectInvite(n.objectId)
            .then((data) => {
              if (data) {
                this.readNotification(n);
              }
            });
        }
      }
    },
    viewDetails(n, skipMarkRead) {
      if (!skipMarkRead && !this.requiresResponse(n)) {
        const newNotificationCount = this.$store.state.notificationsCount - 1;
        this.$store.dispatch("setNotifications", newNotificationCount);
        NotificationService.read(n._id);
      }
      if (
        n.type == this.types.NOTI_POSTLIKED ||
        n.type == this.types.NOTI_POSTOSS ||
        n.type == this.types.NOTI_POSTFIRE ||
        n.type == this.types.NOTI_ACADEMYPOST ||
        n.type == this.types.NOTI_POSTRNC ||
        n.type == this.types.NOTI_COMMENTLIKED ||
        n.type == this.types.NOTI_POSTTAG ||
        n.type == this.types.NOTI_COMMENTTAG ||
        n.type == this.types.NOTI_COMMENTREPLIED ||
        n.type == this.types.NOTI_POSTCOMMENTED ||
        n.type == this.types.NOTI_REPOST
      )
        this.navigateTo({ name: "viewpost", params: { postId: n.objectId } }, n.params);
      else if (
        n.type == this.types.NOTI_NEWACADEMYMEMBER ||
        n.type == this.types.NOTI_ACADEMYLIKED
      )
        this.navigateTo({
          name: "viewacademy",
          params: { academyId: n.objectId },
        });
      else if (n.type == this.types.NOTI_PAYMENTREQUEST && n.originalRecipient) {
        this.readNotification(n);
        this.navigateTo({ name: 'transaction', params: { transactionId: n.objectId } });
      } else if (n.type == this.types.NOTI_PAYMENTREQUEST) {
        this.readNotification(n);
        this.navigateTo({ name: 'transaction', params: { transactionId: n.objectId } });
      } else if (n.type == this.types.NOTI_CC_UPDATE_REQUEST && n.originalRecipient) {
        this.readNotification(n);
        this.navigateTo({ name: 'transaction', params: { transactionId: n.objectId } });
      } else if (n.type == this.types.NOTI_CC_UPDATE_REQUEST) {
        this.navigateTo({
          name: "ccupdate",
          params: { transactionId: n.objectId },
        });
      } else if (n.type == this.types.NOTI_PAYMENTCONFIRMATION)
        this.navigateTo({
          name: "transaction",
          params: { transactionId: n.objectId },
        });
      else if (n.type == this.types.NOTI_DOCUMENTREQUEST || n.type == this.types.NOTI_DOCUMENTREQUESTSURVEY)
        this.navigateTo({
          name: "viewacademydocumentnotification",
          params: { academyDocumentId: n.objectId, notificationId: n._id },
        });
      else if (
        n.type == this.types.NOTI_VIDEOLIKED ||
        n.type == this.types.NOTI_NEWACADEMYVIDEO ||
        n.type == this.types.NOTI_VIDEOCOMMENTED
      )
        this.navigateTo({ name: "viewvideo", params: { videoId: n.objectId } });
      else if (n.type == this.types.NOTI_ACADEMYREVIEWED)
        this.navigateTo({
          name: "academyreview",
          params: { reviewId: n.objectId },
        });
      else if (
        n.type == this.types.NOTI_CLASSREQUESTACCEPTED ||
        n.type == this.types.NOTI_CLASSREQUESTREJECTED ||
        n.type == this.types.NOTI_CLASSREQUEST ||
        n.type == this.types.NOTI_JOINEDCLASS ||
        n.type == this.types.NOTI_CLASSATENDEELEFT ||
        n.type == this.types.NOTI_NEWACADEMYVISITOR
      ) {
        this.navigateTo({
          name: "viewacademyclassnotification",
          params: { academyClassId: n.objectId, notificationId: n._id },
        }, n.params);
      } else if (n.type == this.types.NOTI_ACADEMYMESSAGE ||
        n.type == this.types.NOTI_NEWACADEMYINQUIRY)
        this.navigateTo({
          name: "academymessage",
          params: { messageId: n._id },
        }, n.params);
      else if (n.type == this.types.NOTI_PROGRAMASSIGNED)
        this.navigateTo({ name: "viewprogressdetailsnotification", params: { instanceId: n.objectId, notificationId: n._id } });
      else if (n.type == this.types.NOTI_VIDEOREADY)
        this.navigateTo({
          name: "viewvideo",
          params: { videoId: n.objectId },
        });
      else if (n.type == this.types.NOTI_VIDEODELETED)
        this.navigateTo({
          name: "viewsystemmessage",
          params: { notificationId: n._id },
        });
      else if (
        n.type == this.types.NOTI_NEWEVENTPARTICIPANT ||
        n.type == this.types.NOTI_NEWEVENTMANAGER ||
        n.type == this.types.NOTI_EVENTINVITE
      )
        this.navigateTo({
          name: "viewevent",
          params: { eventId: n.objectId },
        });
      else if (
        n.type == this.types.NOTI_NEWLINK ||
        n.type == this.types.NOTI_EVENTINVITEACCEPTED
      )
        this.navigateTo({
          name: "userprofile",
          params: { userId: n.sender._id },
        });
      else if (n.type == this.types.NOTI_BELTVERIFIED)
        this.navigateTo({
          name: "userprofile",
          params: { userId: n.objectId },
        });
      else if (n.type == this.types.NOTI_BELTREJECTED)
        this.navigateTo({
          name: "edithistory",
        });
      else if (n.type == this.types.NOTI_SAVECOMPETITORALERT)
        this.navigateTo({
          name: "ibjjftournamentstourney",
          params: { tournamentId: n.objectId },
        });
      else
        this.navigateTo({
          name: "userprofile",
          params: { userId: n.sender._id },
        });
    },
    readNotification(n) {
      var data = NotificationService.read(n._id);
      //Change news counter for user on fron and back
      if (data) {
        var i;
        for (i = 0; i < this.notifications.length; i++) {
          const readNoti = this.notifications[i];
          if (readNoti._id == n._id) {
            break;
          }
        }
        this.notifications.splice(i, 1);
        const newNotificationCount = this.$store.state.notificationsCount - 1;
        this.$store.dispatch("setNotifications", newNotificationCount);
        //HACK: Clear push for mobile?
        if (this.$store.state.messagesCount + newNotificationCount == 0) {
          //NotificationService.clearMobile();
        }
        this.notificationsRead.unshift(n);
      } else {
        this.showAlert("error", this.$t("message.oops_not_working"));
      }
    },
    getReadNotifications(append) {
      this.loadingRead = true;
      this.viewMoreRead = false;
      if (!append) this.notificationsRead = [];
      NotificationService.notifications(this.pointerRead, true)
        .then((data) => {
          if (data) {
            this.notificationsRead = this.notificationsRead.concat(
              data.notifications
            );
            this.loadingRead = false;
            this.pointerRead = this.pointerRead + data.count;
            if (data.count >= this.searchesLimit) this.viewMoreRead = true;
          }
        });
    },
    isSystemMessage(type) {
      if (type == this.types.NOTI_VIDEOREADY) {
        return true;
      }
      return false;
    },
    getNewNotifications(append) {
      this.loadingNew = true;
      this.viewMoreNew = false;
      if (!append) this.notifications = [];
      NotificationService.notifications(this.pointerNew, false)
        .then((data) => {
          if (data) {
            this.notifications = this.notifications.concat(data.notifications);
            UserService.updateUser([
              { propName: "notifications", value: this.notifications.length },
            ]);
            this.loadingNew = false;
            this.pointerNew = this.pointerNew + data.count;
            if (data.count >= this.searchesLimit) this.viewMoreNew = true;
          }
        });
    },
  },
};
</script>
<style scoped>
.noti-card {
  overflow: hidden;
}

.noti-row {
  transition: background-color 0.15s ease;
}

.noti-row:hover {
  background-color: rgba(128, 128, 128, 0.06);
}

.noti-row--read {
  opacity: 0.72;
}

.noti-dot-col {
  width: 10px;
  flex-shrink: 0;
}

.noti-unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #1976d2;
  margin-top: 4px;
}

.noti-body-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.noti-divider {
  margin-left: 68px;
}
</style>